/**
 * MetadataRow - Inline row of icon+label metadata items.
 *
 * Used inside object card content components (ContactCardContent,
 * CompanyCardContent, etc.) to render property values with icons.
 * Empty labels are rendered as "–" (en-dash) so every field is always visible.
 */

import type { ComponentType } from 'react';
import { Focusable } from 'react-aria-components';

import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

const EMPTY = '\u2013';

export interface MetadataItem {
  icon?: ComponentType<{ className?: string }>;
  label: string;
  /**
   * Human name of the FIELD the item represents (e.g. "Email", "Industry").
   * The icon is the only visual cue for what a value is \u2014 when set, hovering
   * (or focusing) the icon shows this name in a tooltip.
   */
  fieldName?: string;
  key?: string;
  /**
   * When true, renders a text-shaped skeleton for this item instead of the
   * label or the empty-label placeholder. Use when the item's value is an
   * async resolution (e.g. a contact's "Works at" company that comes from
   * an association) so the row doesn't flash "\u2013" \u2192 real value as the
   * target loads.
   */
  isLoading?: boolean;
}

export interface MetadataRowProps {
  items: MetadataItem[];
  className?: string;
}

export function MetadataRow({ items, className }: MetadataRowProps) {
  return (
    <div
      className={cx(
        'flex items-center gap-3 text-xs text-fg-tertiary',
        className
      )}
    >
      {items.map((item, index) => {
        const Icon = item.icon;
        const displayLabel = item.label.trim() === '' ? EMPTY : item.label;
        const key = item.key ?? `metadata-${index}`;
        const content = (
          <>
            {Icon && (
              <Icon className='w-3.5 h-3.5 shrink-0' aria-hidden='true' />
            )}
            {item.isLoading ? (
              <Skeleton variant='text' width={96} height={12} />
            ) : (
              <span className='truncate'>{displayLabel}</span>
            )}
          </>
        );

        if (!item.fieldName) {
          return (
            <span key={key} className='flex-1 flex items-center gap-1 min-w-0'>
              {content}
            </span>
          );
        }

        // The whole item (icon + value) is the tooltip trigger, so hovering
        // anywhere on the field says what it is. The tooltip's react-aria
        // trigger only attaches hover/focus listeners to a focusable child —
        // Focusable makes the plain span one (adds tabIndex + interaction
        // props); role='img' is one of the roles Focusable accepts.
        //
        // Left-aligned to the field rather than centred: these rows sit in
        // narrow card columns, so a centred tooltip on a short value (an
        // icon plus "12" for headcount) drifts off the field it describes and
        // can overhang the card edge. `top left` anchors it to the field's
        // left edge, and react-aria flips it to bottom-left when there is no
        // room above — so it always reads as belonging to this row.
        return (
          <Tooltip key={key} title={item.fieldName} placement='top left'>
            <Focusable>
              <span
                role='img'
                aria-label={item.fieldName}
                className='flex-1 flex items-center gap-1 min-w-0'
              >
                {content}
              </span>
            </Focusable>
          </Tooltip>
        );
      })}
    </div>
  );
}
